import { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { includes } from 'lodash';
import pLimit from 'p-limit';
import { message } from 'antd5';
import { Button, ModalActionButtons } from 'components/ui';

import { useUploadContext } from 'components/FileUploader';
import {
  getFileDirectoryPath,
  getFileTypesList,
  getIsFileTypeAccepted,
  handleFileRead,
} from 'components/FileUploader/helpers';
import { SelectedFileList } from 'components/FileUploader/Modal/SelectedFileList';
import { FileData, FilteredFiles, UploadFile } from 'components/FileUploader/types';
import {
  CloseOnSuccessProps,
  InspectionUploadWorkflowComponentProps,
  Steps,
} from '../UploadInspectionDataModal';
import { StyledContainer } from './ImageUpload.style';
import { getBulkUploadEventTypes } from '../../utils';
import { AtlasGqlFileFormat } from 'types/atlas-graphql';
import { notifyBugsnag } from 'utils/bugsnag';
import { CSV_BULK_UPLOAD_OPTIONS, JSON_BULK_UPLOAD_OPTIONS } from 'utils/constants';
import { useGetIngestUploadUrls } from 'utils/ingest';
import { notEmpty } from 'utils/types';
import { useAccountContext } from 'utils/account/AccountContext';
import { Checkbox } from 'antd5';

const limit = pLimit(10);

const ACCEPTED_IMAGE_FILE_TYPES = 'image/jpeg';

export const ImageUpload: React.FunctionComponent<
  InspectionUploadWorkflowComponentProps & {
    onBack: () => void;
    onSuccess: (input: CloseOnSuccessProps) => void;
    fileType: AtlasGqlFileFormat;
    setUseFolderMethod: Dispatch<SetStateAction<boolean>>;
    // UUID of a single inspection, only populated if working with exactly one inspection
    singleInspectionId?: string;
    // Boolean flag that's true if the user did not upload an image details file
    skippedDetails?: boolean;
  }
> = ({
  uploadId,
  step,
  setStep,
  onBack,
  onSuccess,
  fileType,
  setUseFolderMethod,
  singleInspectionId,
  skippedDetails = false,
}) => {
  const [filteredFilesStatus, setFilteredFilesStatus] = useState<FilteredFiles>({
    count: 0,
    status: false,
  });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [inspectionUploadEventTypes, setInspectionUploadEventTypes] = useState(() =>
    getBulkUploadEventTypes(CSV_BULK_UPLOAD_OPTIONS.CSV_BULK)
  );
  // Need an axios cancel token stored as a ref
  const source = useRef(axios.CancelToken.source());
  const cancelRequest = (reason: string) => {
    if (source.current) {
      source.current.cancel(
        `Upload failed. Please try again or contact support. Error message: ${reason}`
      );
    }
  };

  const { features } = useAccountContext();
  const { FOLDER_DETAILS_UPLOAD: hasFolderUploadFlag } = features;

  // Hook to allow us to generate the URLs to upload images to S3
  const { getIngestUploadUrls, getMediaServiceUploadUrls } = useGetIngestUploadUrls();

  const {
    // List of names of the images that could not be read
    corruptImgFiles,
    setCorruptImgFiles,
    // List of all files selected
    selectedFiles,
    setSelectedFiles,
    // Sets the uploading flag (also used by the SelectedFileList)
    setUploading,
    // Boolean indicator of whether we are currently attempting to upload files
    uploading,
    // Filtered version of selectedFiles where valid = true
    validFiles,
  } = useUploadContext();

  // Need to recalculate options for upload event types whenever fileType changes
  useEffect(() => {
    const inspectionType = {
      [AtlasGqlFileFormat.Csv]: CSV_BULK_UPLOAD_OPTIONS.CSV_BULK,
      [AtlasGqlFileFormat.Json]: JSON_BULK_UPLOAD_OPTIONS.JSON_BULK,
    }[fileType];

    setInspectionUploadEventTypes(() => getBulkUploadEventTypes(inspectionType));
  }, [fileType]);

  // Whenever selectedFiles changes, check to see if we should swap to the validation step
  useEffect(() => {
    if (selectedFiles.length > 0) {
      setStep(Steps.ImageUploadValidation);
    }
    // Don't need to retrigger on setStep change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFiles]);

  const uploadImagesButtonDisabled = useMemo(() => {
    return uploading || validFiles.length > 0;
  }, [validFiles, uploading]);

  /**
   * When navigating back we either want to
   * Go back to details
   * Go from image validation to upload
   * Cancel the upload process and go back to validation
   */
  const handleBack = () => {
    if (uploading) {
      setErrorMessage('Upload was canceled');
      cancelRequest('User canceled request');
    } else if (step === Steps.ImageUploadValidation) {
      setSelectedFiles([]);
      setCorruptImgFiles([]);
      setStep(Steps.ImageUpload);
    } else {
      onBack();
    }
    setUploading(false);
  };

  // Callback to remove files, passed into the SelectedFileList
  const removeSelectedFile = (filename: string) => {
    setSelectedFiles(selectedFiles.filter(file => file.sourceName !== filename));
    // If there's only one file left, and we remove it, we're essentially navigating back to the upload step
    if (selectedFiles.length === 1 && step === Steps.ImageUploadValidation) {
      handleBack();
    }
  };

  // Run for each individual file that is uploaded. Used to set the state to 'completed'
  // So that the SelectedFileList can give real-time updates on uploading progress
  const handleSingleFileUploadComplete = (fileName: string) => {
    setSelectedFiles(previousSelectedFiles =>
      previousSelectedFiles.map(f =>
        f.sourceName === fileName ? { ...f, status: 'completed' } : f
      )
    );
  };

  /**
   * Once files have been selected, check for corrupt files, format into the UploadFile type,
   * and further filter out DS_Store files and anything else that
   */
  const handleFileSelection = async ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    let corruptFiles: string[] = [];
    const targetFiles = target.files ?? [];
    if (targetFiles.length) {
      const imgFiles = Array.from(targetFiles).filter(f => f.type === 'image/jpeg');

      try {
        // Wait for all files to be read
        corruptFiles = (await Promise.all(imgFiles.map(handleFileRead))).filter(notEmpty);

        if (corruptFiles.length > 0) {
          // Set this value to display the number of corrupt files in the file list
          setCorruptImgFiles(corruptFiles);
        }
      } catch (error) {
        console.error('Error reading files:', error);
      }
    }

    // Map the target files to the expected UploadFile type used by the Upload Context
    const files: UploadFile[] = Array.from(targetFiles ?? [], f => {
      const path = getFileDirectoryPath((f as FileData).webkitRelativePath ?? '');
      const name = f.name;
      const sourceName = `${path}${name}`;

      const uploadFile: UploadFile = {
        data: f as FileData,
        name: sourceName,
        path,
        percent: 0,
        sourceName,
        type: 'directory',
        valid: true,
      };
      return uploadFile;
    });

    setSelectedFiles(files);

    let dsStoreCount = 0;
    setSelectedFiles(prevSelectedFiles => {
      return prevSelectedFiles
        .map(f => {
          const name = f.data?.name;
          // Check for DS_Store files
          if (name === '.DS_Store') {
            dsStoreCount++;
            return undefined;
          }

          // In the case of MultipleInspection uploads this will reset files back to `true` if they should be looked at.
          const fileTypesArray: string[] = getFileTypesList(ACCEPTED_IMAGE_FILE_TYPES);
          const fileType = f.data?.type;
          f.valid = getIsFileTypeAccepted(fileTypesArray, false, '', fileType);

          // Corrupt: Check if file is in corrupt files list. Skip if we are already invalid
          if (f.valid && corruptFiles.length > 0) {
            const filename = f.name;

            f.valid = !corruptFiles.includes(filename);
          }
          // return object includes f.valid boolean: true if it passes the above tests
          // Set key to sourceName to make sure we include the path
          return {
            ...f,
            key: f.sourceName,
          };
        })
        .filter(notEmpty);
    });

    // The SelectedFileList displays DS_Store file counts separate, so set that status here
    setFilteredFilesStatus({
      count: dsStoreCount,
      status: dsStoreCount > 0,
    });
  };

  /**
   * When actually submitting the selected files, process the validFiles from upload context and upload them
   */
  const handleUploadSubmit = async () => {
    // SelectedFileList always shows all selected files, so if we just want to work with the valid files,
    // set selectedFiles to only valid files
    setSelectedFiles(previousSelectedFiles => previousSelectedFiles.filter(f => f.valid));
    if (validFiles.length > 0) {
      setUploading(true);

      // Get signed url for upload
      const filePathsWithNames = validFiles.map(file => {
        // Map to sourceName (which includes path) to properly copy directory structure in S3
        return { fileName: file.sourceName };
      });

      const dataType = inspectionUploadEventTypes.inspectionType;
      // If we have skipped details, the record id should be the inspection id, otherwise use the upload id
      const recordId = skippedDetails && singleInspectionId ? singleInspectionId : uploadId;
      const ingestInput = {
        bundleId: recordId,
        dataType,
        // Set prefix to dataType to be consistent with other file uploads
        prefix: dataType,
        files: filePathsWithNames,
        recordId: recordId,
      };

      // If we've skipped details, we need to manually add the images to the media service bucket, not the ingest bucket
      const { items } =
        skippedDetails && singleInspectionId
          ? await getMediaServiceUploadUrls(ingestInput)
          : await getIngestUploadUrls(ingestInput);

      // If we don't get anything, throw an error
      if (!items || !items.length) {
        message.error('Error uploading inspections: Upload URL could not be obtained');
        setUploading(false);
        return;
      }

      // Reformat the provided files to match the expected shape
      const itemsToFiles = validFiles.map(file => {
        const foundItem = items.find(item => includes(item.fields['key'], file.name));
        return { ...foundItem, file: file.data, sourceName: file.sourceName };
      });

      try {
        // This operation is a modified copy of the general  from ingest utils
        let cancelled: boolean = false;
        const fileUploads = itemsToFiles.map(item =>
          limit(async () => {
            // If the cancelled flag is set, don't attempt to perform
            // any more uploads.  If this is not here, all subsequent uploads
            // will be attempted and immediately fail due to the cancel token being
            // set.
            if (cancelled) {
              return;
            }
            // Also skip if we're missing file, or fields
            const { url, file, fields, sourceName: itemSourceName } = item;
            if (!file || !fields || !url) {
              console.error(`Missing data:`);
              return;
            }

            const formData: FormData = new FormData();

            Object.keys(fields).forEach(key => {
              const value = fields[key];
              formData.append(key, value);
            });

            formData.append('file', file as File);

            try {
              setSelectedFiles(previousSelectedFiles =>
                previousSelectedFiles.map(f => {
                  return f.sourceName === itemSourceName ? { ...f, status: 'uploading' } : f;
                })
              );
              await axios.post(url, formData, {
                headers: {
                  'Content-Type': 'multipart/form-data',
                },
                cancelToken: source.current.token,
              });

              if (!cancelled) {
                await handleSingleFileUploadComplete(item.sourceName);
              }
            } catch (error: any) {
              const message = `[File Upload error]: ${JSON.stringify(error)}`;
              notifyBugsnag(message);
              if (axios.isCancel(error)) {
                // set cancelled flag to prevent more upload attempts
                cancelled = true;
                setUploading(false);
                setErrorMessage(error.message);
                throw error.message;
              } else {
                setSelectedFiles(previousSelectedFiles =>
                  previousSelectedFiles.map(f =>
                    f.sourceName === file.name ? { ...f, status: 'error' } : f
                  )
                );
              }
            }
          })
        );

        await Promise.all(fileUploads);
      } catch (error) {
        message.error(`Error uploading images: ${error.message}`);
        setUploading(false);
        return;
      }
    }
    // If we get here, the files were uploaded successfully
    setUploading(false);
    handleUploadComplete();
  };

  const handleUploadComplete = () => {
    onSuccess({ uploadedImages: validFiles });
  };

  return (
    <>
      <p>Please select a folder that contains inspection images to upload.</p>
      <p>
        NOTE: SkySpecs recommends uploading images via URL for file transfers greater than 4GB for
        speed and stability.
      </p>
      <StyledContainer>
        <label //@ts-ignore - the disabled property isn't supported by label but will be used by the button styling
          disabled={uploadImagesButtonDisabled}
          className={`ant-btn ant-btn-primary fileUploader`}
          htmlFor={'file-uploader'}
        >
          Upload Images
        </label>
        <input
          disabled={uploadImagesButtonDisabled}
          id={'file-uploader'}
          multiple={true}
          name={'file-uploader'}
          onChange={handleFileSelection}
          type="file"
          {...{ webkitdirectory: 'true', directory: 'true' }}
        />
      </StyledContainer>
      {step === Steps.ImageUploadValidation && (
        <SelectedFileList
          validFiles={validFiles}
          fileName={'testing'}
          uploading={uploading}
          errorMessage={errorMessage}
          filteredFiles={filteredFilesStatus}
          corruptFiles={corruptImgFiles}
          onRemove={removeSelectedFile}
          width={null}
        />
      )}
      {skippedDetails && singleInspectionId && hasFolderUploadFlag && (
        <Checkbox
          data-testid={'folder-method-checkbox'}
          onChange={e => {
            setUseFolderMethod(e.target.checked);
          }}
        >
          Use folder structure to determine image details
        </Checkbox>
      )}
      <ModalActionButtons>
        <Button data-testid="inspection-image-upload-cancel-btn" onClick={handleBack}>
          Back
        </Button>
        <Button
          disabled={uploading || validFiles.length === 0}
          data-testid="inspection-image-upload-upload-btn"
          onClick={handleUploadSubmit}
          loading={uploading}
        >
          Upload
        </Button>
      </ModalActionButtons>
    </>
  );
};
